<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { asq, toDictionary } from '@sembo-private/asq';
import { storeToRefs } from 'pinia';
import { getDomain } from '@sembo-private/utils';
import { MoneyFormatter } from '@sembo-private/money';

import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
// TODO: Fix old states dependency in enclosed module
// eslint-disable-next-line boundaries/element-types
import { RecipeStateKey } from '@apps/main/modules/state/state';
import {
  getFormattedDateTime,
  getFormattedTime,
} from '@apps/main/modules/helpers/formatting';
import { usePreferredTimeStore } from '@apps/main/modules/store/preferredTimeStore';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores/browserStore';

import type {
  ActivityTicket,
  AgeRangeQL,
  Money,
  TicketRateQL,
  TimeSlotQL,
  ValueId,
} from '@libs/trip-planner-interfaces';
import { AccommodationType } from '@libs/trip-planner-interfaces';
import type { AppSelectOption } from '@libs/ui';
import { AppSvgIcon, AppSelectLight, AppNumberInput } from '@libs/ui';
import { assertValue } from '@libs/utils';

import { getAgeRangeAmountText } from '../../stores/activityDetailsTicketsStoreLogic';

const browserStore = useBrowserStore();

interface TicketProps {
  ticketRate: TicketRateQL;
  productCode: string;
  dateOffset: number;
  initialRateTickets: ActivityTicket[];
  activityType: AccommodationType;
}

const props = defineProps<TicketProps>();

interface TicketEmits {
  (eventName: 'update-selected-status', status: boolean): void;
}

const emit = defineEmits<TicketEmits>();

const preferredTimeStore = usePreferredTimeStore();
const { preferredDay } = storeToRefs(preferredTimeStore);
const recipeState = injectStrict(RecipeStateKey);

const selectedTicketsByAgeRange = ref<Record<string, ActivityTicket>>(
  asq(props.initialRateTickets)
    .pipe(toDictionary((activityTicket) => activityTicket.ageRangeId))
    .single()
);

const selectedTimeSlot = ref(getInitialSelectedTimeSlot());
function getInitialSelectedTimeSlot(): ValueId | undefined {
  const id =
    props.initialRateTickets[0]?.timeSlot?.id ??
    props.ticketRate.timeSlots?.[0]?.timeSlotId ??
    undefined;

  return id
    ? {
        id,
        value: props.ticketRate.timeSlots?.[0]
          ? getFormattedTime(props.ticketRate.timeSlots[0].startTime)
          : undefined,
      }
    : undefined;
}

const totalAmountTicketsSelected = computed(() =>
  Object.values(selectedTicketsByAgeRange.value).reduce(
    (total, current) => (total += current.quantity),
    0
  )
);

const numberTicketsLeft = computed(() =>
  props.ticketRate.maximumAmountTickets
    ? props.ticketRate.maximumAmountTickets - totalAmountTicketsSelected.value
    : undefined
);

function incrementAgeRangeRate(ageRangeId: string): void {
  if (!(ageRangeId in selectedTicketsByAgeRange.value)) {
    selectedTicketsByAgeRange.value[ageRangeId] = {
      ageRangeId,
      quantity: 0,
      rate: {
        id: props.ticketRate.rateId,
        value: props.ticketRate.title,
      },
      timeSlot: selectedTimeSlot.value,
      code: props.productCode,
      dateOffset: props.dateOffset,
    };
  }

  const activityTicket = selectedTicketsByAgeRange.value[ageRangeId];
  assertValue(
    activityTicket,
    'RateDescription.vue > incrementAgeRangeRate > activityTicket'
  );

  if (props.ticketRate.maximumAmountTickets !== undefined) {
    if (activityTicket.quantity === props.ticketRate.maximumAmountTickets) {
      return;
    }
    if (activityTicket.quantity === props.ticketRate.maximumAmountTickets) {
      return;
    }
  }

  activityTicket.quantity++;

  calculateAndEmitStatus();
}

function decrementAgeRangeRate(ageRangeId: string): void {
  if (!(ageRangeId in selectedTicketsByAgeRange.value)) {
    return;
  }

  const activityTicket = selectedTicketsByAgeRange.value[ageRangeId];
  assertValue(
    activityTicket,
    'RateDescription.vue > decrementAgeRangeRate > activityTicket'
  );

  if (activityTicket.quantity > 1) {
    activityTicket.quantity--;
    return;
  }

  if (activityTicket.quantity === 1) {
    delete selectedTicketsByAgeRange.value[ageRangeId];
  }

  calculateAndEmitStatus();
}

function calculateAndEmitStatus(): void {
  const numberOfSelectedTickets = Object.values(
    selectedTicketsByAgeRange.value
  ).length;
  const isTicketRateValid =
    numberOfSelectedTickets === 0
      ? true
      : isQuantityValid() &&
        isRequiredAgeRangesValid() &&
        isEveryAgeRangeValid();

  emit('update-selected-status', isTicketRateValid);
}

function selectTimeSlot(event: AppSelectOption<string>): void {
  selectedTimeSlot.value = {
    id: event.id,
    value: event.label,
  };

  if (selectedTicketsByAgeRange.value) {
    for (let criteria of Object.values(selectedTicketsByAgeRange.value)) {
      criteria.timeSlot = {
        id: selectedTimeSlot.value.id,
        value: selectedTimeSlot.value.value,
      };
    }
  }
}

function getFormattedTimeSlot(timeSlot: TimeSlotQL): string {
  return preferredDay.value
    ? getFormattedDateTime(timeSlot.startTime)
    : getFormattedTime(timeSlot.startTime);
}

watch(selectedTicketsByAgeRange.value, () => {
  recipeState.updateSelectedTickets(
    Object.values(selectedTicketsByAgeRange.value),
    props.ticketRate.rateId,
    props.dateOffset
  );
});

function isQuantityValid(): boolean {
  return (
    totalAmountTicketsSelected.value === 0 ||
    totalAmountTicketsSelected.value >=
      (props.ticketRate.minimumAmountTickets ?? 0)
  );
}

function isRequiredAgeRangesValid(): boolean {
  return (
    !props.ticketRate.requiredAgeRanges ||
    props.ticketRate.requiredAgeRanges.some((id: string) => {
      const ticketQuantity = selectedTicketsByAgeRange.value[id]?.quantity;
      return ticketQuantity !== undefined ? ticketQuantity > 0 : false;
    }) ||
    Object.keys(selectedTicketsByAgeRange.value).length === 0
  );
}

function isEveryAgeRangeValid(): boolean {
  return props.ticketRate.ageRanges.every((ageRange: AgeRangeQL) => {
    const quantitySelected =
      selectedTicketsByAgeRange.value[ageRange.ageRangeId]?.quantity ?? 0;
    const minimum = ageRange?.minimumTicketsPerAgeRange ?? 0;

    return quantitySelected >= minimum;
  });
}

function getTimeSlotOption(): AppSelectOption<string>[] {
  if (props.ticketRate.timeSlots) {
    return props.ticketRate.timeSlots.map((timeSlot) => ({
      id: timeSlot.timeSlotId,
      label: getFormattedTimeSlot(timeSlot),
    }));
  }

  return [];
}

function getSelectedTimeSlot(): AppSelectOption<string> | undefined {
  return getTimeSlotOption().find(
    (option) => option.id === selectedTimeSlot.value?.id
  );
}

function getPriceForAgeRange(ageRange: AgeRangeQL, timeSlotId?: string): Money {
  const timeSlotPrice = ageRange.availableTimeslotsPrices?.find(
    (timeSlotPrice) => timeSlotPrice.availabletimeSlotId === timeSlotId
  );

  return timeSlotPrice ?? ageRange.price;
}
</script>

<template>
  <div class="ticket">
    <div class="ticket-header">
      <div class="header-icon">
        <AppSvgIcon
          :name="
            props.activityType === AccommodationType.Golf ? 'golf' : 'activity'
          "
        />
      </div>
      {{ props.ticketRate.title }}
    </div>
    <div class="rate-content">
      <div v-if="props.ticketRate.timeSlots" class="select-time-container">
        <AppSelectLight
          :options="getTimeSlotOption()"
          :selected="getSelectedTimeSlot()"
          @select="selectTimeSlot"
        />
      </div>
      <div
        v-for="ageRange in props.ticketRate.ageRanges"
        :key="ageRange.ageRangeId"
        class="age-range-wrapper"
      >
        <div class="age-range-description-container">
          <div class="age-range-description">
            <span class="age-range-label">
              {{ ageRange.description }}
            </span>
          </div>

          <div class="amount-tickets-wrapper">
            <span v-if="!browserStore.isSmallDevice" class="age-range-price">
              <!-- TODO: long term future proof solution would be to look into MoneyFormatter component and check if it is using global currency and if it can be switched to use product currency only, since this situation applies to any product in Sembo -->
              {{
                MoneyFormatter.instance.formatWithCurrency(
                  getPriceForAgeRange(ageRange, selectedTimeSlot?.id).amount,
                  getPriceForAgeRange(ageRange, selectedTimeSlot?.id).currency,
                  getDomain.getCurrentDomainSpec().FullLocale
                )
              }}
            </span>
            {{ getAgeRangeAmountText(ageRange, selectedTimeSlot?.id) }}
          </div>
          <span v-if="browserStore.isSmallDevice" class="price-mobile">
            {{
              MoneyFormatter.instance.formatWithCurrency(
                getPriceForAgeRange(ageRange, selectedTimeSlot?.id).amount,
                getPriceForAgeRange(ageRange, selectedTimeSlot?.id).currency,
                getDomain.getCurrentDomainSpec().FullLocale
              )
            }}
          </span>
        </div>
        <AppNumberInput
          :value="selectedTicketsByAgeRange[ageRange.ageRangeId]?.quantity ?? 0"
          :only-stepwise-input="true"
          :disable-decrease="
            !selectedTicketsByAgeRange[ageRange.ageRangeId]?.quantity
          "
          :disable-increase="
            totalAmountTicketsSelected ===
              props.ticketRate.maximumAmountTickets ||
            selectedTicketsByAgeRange[ageRange.ageRangeId]?.quantity ===
              ageRange.quantityAvailable
          "
          @decrease="decrementAgeRangeRate(ageRange.ageRangeId)"
          @increase="incrementAgeRangeRate(ageRange.ageRangeId)"
        />
      </div>
      <div v-if="!!props.ticketRate.maximumAmountTickets" class="summary">
        <div class="summary-text">
          {{
            $t('planner.tickets_available', [
              props.ticketRate.maximumAmountTickets,
            ])
          }}
          <span class="tickets-remaining">
            {{ $t('planner.tickets_remaining', [numberTicketsLeft]) }}
          </span>
        </div>
        <span class="validation-error">
          <AppSvgIcon
            name="warningCircle"
            color="color-tertiary-20"
            class="warning-circle-icon"
          />
          {{ $t('planner.tickets_validation_error') }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ticket {
  border-radius: var(--border-radius-8);
  padding: var(--spacing-32);
  box-sizing: border-box;
  background-color: var(--color-white);
  border: 1px solid var(--color-secondary-85);
  display: flex;
  flex-direction: column;

  .ticket-header {
    margin-bottom: var(--spacing-32);
    font: var(--font-label-xxl-bold);
    display: flex;
    align-items: center;
    .header-icon {
      margin-right: var(--spacing-24);
    }
  }
  .rate-content {
    padding-left: var(--spacing-48);
    box-sizing: border-box;
    .age-range-description-container {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      margin-bottom: var(--spacing-24);
    }
    .age-range-wrapper {
      display: flex;
      justify-content: space-between;
      .age-range-description {
        display: flex;

        .age-range-label {
          font: var(--font-label-xl-bold);
        }

        .age-range-price {
          margin: var(--spacing-4) 0;
          color: var(--color-secondary-60);
        }
      }
    }
    .amount-tickets-wrapper {
      @include font-label-l-light;
      color: var(--color-secondary-60);
      margin-top: var(--spacing-4);
    }
    .line-spacer {
      margin: 0px var(--spacing-8);
    }
    .select-time-container {
      width: 140px;
      padding-bottom: var(--spacing-32);
    }

    .summary {
      display: flex;
      justify-content: space-between;
      gap: var(--spacing-16);

      .tickets-remaining {
        margin-left: var(--spacing-4);
        color: var(--color-secondary-60);
      }

      .validation-error {
        margin-left: auto;
        // TODO_REBRAND: Color not known yet
        color: #e1bc2f;
        display: flex;
        align-items: center;

        .warning-circle-icon {
          margin-right: var(--spacing-4);
        }
      }
    }
  }
}

@include for-medium-devices-max {
  .ticket {
    padding: var(--spacing-16);
    padding-top: var(--spacing-32);

    .ticket-header {
      margin-bottom: var(--spacing-32);
    }

    .rate-content {
      padding: 0;

      .age-range-description {
        display: flex;
        .age-range-price {
          margin-top: var(--spacing-4);
        }
      }

      .price-mobile {
        margin-top: var(--spacing-4);
        @include font-label-l-light;
      }

      .amount-tickets-wrapper {
        align-items: flex-start;
        margin-top: var(--spacing-8);
      }

      .summary {
        flex-flow: column;
        align-items: center;

        .summary-text {
          margin-top: var(--spacing-8);
        }

        .validation-error {
          margin-left: unset;
        }
      }
    }
  }
}
</style>
